import { Roles } from '@/utils';
import * as Sentry from '@sentry/react';
import { LogoIcon } from '@commons/logo';
import { useRole } from '@hooks/useRole';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { setFavicon } from '@PorkyCol/utils';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { useEffect } from 'react';
import { StatsigProvider } from 'statsig-react';
import Drawer from '@modules/drawers/page';
import { usePrintQuery } from '@/context/user-notifications/hooks/usePrintQuery';
import { UserNotificationsProvider } from '@/context/user-notifications/UserNotificationsProvider';
import { Router } from './routes';
import ThemeProvider from './theme';

import 'simplebar-react/dist/simplebar.min.css';
import ScrollToTop from './commons/scroll-to-top';
import SnackbarProvider from './commons/snackbar';
import { useLocalStorage } from './hooks/useLocalStorage';
import { UserStore } from './store/user';
import { print } from './utils/printer';
import { setupTokenRefresh } from './utils/tokenRefresh';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.tz.setDefault('America/Bogota');

const getStateSocketCommander = localStorage.getItem('socket-commander') === 'true';
const getStateSocketTicket = localStorage.getItem('socket-ticket') === 'true';
// const socket = io(import.meta.env.VITE_HOST_API_KEY, {
//   path: `${import.meta.env.VITE_SOCKET_PORT}/socket.io`,
//   autoConnect: getStateSocketCommander ?? getStateSocketTicket,
// });

function App() {
  const {
    prints: printsCommander,
    startRealtimeUpdates: startRealtimeUpdatesCommander,
    stopRealtimeUpdates: stopRealtimeUpdatesCommander,
    markAsRead: markAsReadCommander,
  } = usePrintQuery('COMMANDER');
  const {
    prints: printsTicket,
    startRealtimeUpdates: startRealtimeUpdatesTicket,
    stopRealtimeUpdates: stopRealtimeUpdatesTicket,
    markAsRead: markAsReadTicket,
  } = usePrintQuery('TICKET');
  const [flagSocketCommander] = useLocalStorage<boolean>({
    key: 'socket-commander',
    defaultValue: getStateSocketCommander,
  });
  const [flagSocketTicket] = useLocalStorage<boolean>({
    key: 'socket-ticket',
    defaultValue: getStateSocketTicket,
  });
  const { user } = UserStore.getState();
  const role = useRole([Roles.ATM, Roles.ADMIN]);

  // useEffect(() => {
  //   socket.connect();
  //
  //   return () => {
  //     socket.disconnect();
  //   };
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, []);

  function onFooEvent(value: any[]) {
    if (!role) return;
    value.forEach((item) => {
      const data = item.metadata;
      if (data.type === 'commander') {
        print(data, data.type);
        markAsReadCommander(item.id);
      } else if (data.type === 'ticket') {
        print({ ...data, companyId: user?.companySelected?.id }, data.type);
        markAsReadTicket(item.id);
      }
    });
  }

  const connect = () => {
    if (flagSocketCommander && user?.branchSelected?.id) {
      startRealtimeUpdatesCommander(`${user?.branchSelected?.id}`);
    }
    if (flagSocketTicket && user?.branchSelected?.id) {
      startRealtimeUpdatesTicket(`${user?.branchSelected?.id}`);
    }
  };
  const disconnect = () => {
    if (flagSocketCommander) {
      stopRealtimeUpdatesCommander();
    }
    if (flagSocketTicket) {
      stopRealtimeUpdatesTicket();
    }
  };

  useEffect(() => {
    // disconnect();
    if (user?.branchSelected?.id) {
      connect();
    }

    return () => {
      disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, flagSocketCommander, flagSocketTicket]);
  useEffect(() => {
    if (flagSocketCommander && printsCommander?.length) {
      onFooEvent(printsCommander);
    }
    if (flagSocketTicket && printsTicket?.length) {
      onFooEvent(printsTicket);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flagSocketCommander, flagSocketTicket, printsCommander, printsTicket]);

  useEffect(() => {
    setFavicon(LogoIcon, import.meta.env.VITE_COLOR_BUSINESS);
    setupTokenRefresh();
  }, []);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <ThemeProvider>
        <ScrollToTop />
        <SnackbarProvider>
          <StatsigProvider
            sdkKey={import.meta.env.VITE_CLIENT_KEY}
            waitForInitialization
            user={{}}
            options={{
              environment: { tier: import.meta.env.VITE_TIER },
            }}
          >
            <UserNotificationsProvider>
              <Router />
              <Drawer />
            </UserNotificationsProvider>
          </StatsigProvider>
        </SnackbarProvider>
      </ThemeProvider>
    </LocalizationProvider>
  );
}

export default Sentry.withProfiler(App);
